import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileTextIcon, StethoscopeIcon, PillIcon, BrainIcon, ScaleIcon, BookOpenIcon, LayersIcon, PackageIcon, ChevronRightIcon } from 'lucide-react';
const smallCards = [{
  title: 'Free Doctor\nConsultation',
  icon: <StethoscopeIcon className="w-5 h-5 md:w-7 md:h-7 text-teal-500" data-id="element-2284" data-mp="3nbwcatj8zmhz" />,
  link: '/doctor-consultation',
  badge: {
    text: 'FREE',
    color: 'bg-[#FF9F43]'
  },
  iconBg: 'bg-teal-50'
}, {
  title: 'Ask a\nPharmacist',
  icon: <PillIcon className="w-5 h-5 md:w-7 md:h-7 text-orange-500" data-id="element-2285" data-mp="f22zv5xsi5io" />,
  link: '/pharmacist-consultation',
  badge: {
    text: 'FREE',
    color: 'bg-[#FF9F43]'
  },
  iconBg: 'bg-orange-50'
}, {
  title: 'AI Nutrition\n& Diet',
  icon: <BrainIcon className="w-5 h-5 md:w-7 md:h-7 text-purple-500" data-id="element-2286" data-mp="17l0ak7tx5pox" />,
  link: '/nutrition-calculator',
  badge: {
    text: 'New',
    color: 'bg-[#FF4757]'
  },
  iconBg: 'bg-purple-50'
}, {
  title: 'BMI\nCalculator',
  icon: <ScaleIcon className="w-5 h-5 md:w-7 md:h-7 text-green-500" data-id="element-2287" data-mp="qtymtb45npuo" />,
  link: '/bmi-calculator',
  badge: {
    text: 'New',
    color: 'bg-[#FF4757]'
  },
  iconBg: 'bg-green-50'
}, {
  title: 'Ingredient\nGlossary',
  icon: <BookOpenIcon className="w-5 h-5 md:w-7 md:h-7 text-blue-500" data-id="element-2288" data-mp="h22qa4hpw7sk" />,
  link: '/ingredient-glossary',
  badge: {
    text: 'COMING SOON',
    color: 'bg-blue-500'
  },
  iconBg: 'bg-blue-50'
}, {
  title: 'Stacking\nGuide',
  icon: <LayersIcon className="w-5 h-5 md:w-7 md:h-7 text-amber-500" data-id="element-2289" data-mp="3q8tzp8rymvto" />,
  link: '/stacking-guide',
  badge: {
    text: 'COMING SOON',
    color: 'bg-blue-500'
  },
  iconBg: 'bg-amber-50'
}, {
  title: 'Bundle\nBuilder',
  icon: <PackageIcon className="w-5 h-5 md:w-7 md:h-7 text-pink-500" data-id="element-2290" data-mp="1ohpaejdi27ci" />,
  link: '/bundle-builder',
  badge: {
    text: 'COMING SOON',
    color: 'bg-blue-500'
  },
  iconBg: 'bg-pink-50'
}];
export function FeatureCardsSection() {
  const navigate = useNavigate();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasMoved, setHasMoved] = useState(false);
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!sliderRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
    setHasMoved(false);
  };
  const handleMouseLeave = () => {
    setIsDragging(false);
  };
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !sliderRef.current) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    sliderRef.current.scrollLeft = scrollLeft - walk;
    if (Math.abs(walk) > 5) {
      setHasMoved(true);
    }
  };
  const handleCardClick = (link: string) => {
    if (!hasMoved) {
      navigate(link);
    }
  };
  return <>
      <div className="container mx-auto px-4" data-id="element-2291" data-mp="k6wjpjs74i8h">
        {/* Section Header */}
        <div className="mb-4" data-id="element-2292" data-mp="o91bse2zx6t0">
          <div className="flex items-center gap-2 mb-1" data-id="element-2293" data-mp="oa2n8jnxbvb9">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900" data-id="element-2294" data-mp="o6on7i2qfy6z">
              Pharmacy Services
            </h2>
            <span className="bg-[#1E2B55] text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-2295" data-mp="y6ns94zy16f3">
              24/7
            </span>
          </div>
          <p className="text-sm md:text-base text-gray-600" data-id="element-2296" data-mp="12ypbxbzfxzf5">
            From your home, order your prescription or consult a doctor or
            pharmacist!
          </p>
        </div>

        {/* Services Container */}
        <div className="bg-[#F4FBF7] rounded-2xl md:rounded-3xl p-3 md:p-6 border border-green-50" data-id="element-2297" data-mp="1bwrc5pbcoo1d">
          <div className="flex flex-col gap-3 md:gap-4" data-id="element-2298" data-mp="13aye73myv8rb">
            {/* Top Row: Prescription */}
            <button onClick={() => navigate('/prescription')} className="bg-white rounded-xl md:rounded-2xl p-4 flex items-center gap-4 hover:shadow-md transition-shadow text-left w-full group" data-id="element-2299" data-mp="z9ctwx83rs73">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0 relative overflow-hidden" data-id="element-2300" data-mp="181etyue9f6gu">
                <div className="absolute inset-0 bg-green-100 opacity-50 transform -skew-x-12" data-id="element-2301" data-mp="kdx4x6mdqmhk"></div>
                <motion.div animate={{
                scale: [1, 1.1, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="relative z-10" data-id="element-2302" data-mp="gcv2kgx61ubd">
                  <FileTextIcon className="w-6 h-6 md:w-8 md:h-8 text-green-500" data-id="element-2303" data-mp="20pamdmwsqc7t" />
                </motion.div>
              </div>
              <div className="flex-1 min-w-0" data-id="element-2304" data-mp="3imnrsapqz545">
                <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-0.5 md:mb-1" data-id="element-2305" data-mp="3nnl78fjdhl1y">
                  Order via Prescription
                </h3>
                <p className="text-xs md:text-sm text-gray-500 line-clamp-2 md:line-clamp-none" data-id="element-2306" data-mp="39p504nhllsja">
                  Easily order your medications, upload your doctor's
                  prescription.
                </p>
              </div>
              <div className="flex items-center gap-1 text-green-500 font-bold text-sm md:text-base flex-shrink-0 group-hover:translate-x-1 transition-transform" data-id="element-2307" data-mp="a7xaqs6t14em">
                Upload <ChevronRightIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-2308" data-mp="3sk23jx0q75q4" />
              </div>
            </button>

            {/* Bottom Row: Remaining Services */}
            <div className="relative group/slider" data-id="element-2309" data-mp="p4o16byi35s3">
              {/* Navigation Arrows - Desktop Only */}
              <button onClick={() => {
              const el = document.getElementById('pharmacy-services-slider');
              if (el) el.scrollBy({
                left: -200,
                behavior: 'smooth'
              });
            }} className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg items-center justify-center text-gray-400 hover:text-green-500 transition-colors opacity-0 group-hover/slider:opacity-100" aria-label="Previous" data-id="element-2310" data-mp="twxwk7idwy1r">
                <ChevronRightIcon className="w-6 h-6 rotate-180" data-id="element-2311" data-mp="g0hatm7uwxtv" />
              </button>
              <button onClick={() => {
              const el = document.getElementById('pharmacy-services-slider');
              if (el) el.scrollBy({
                left: 200,
                behavior: 'smooth'
              });
            }} className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg items-center justify-center text-gray-400 hover:text-green-500 transition-colors opacity-0 group-hover/slider:opacity-100" aria-label="Next" data-id="element-2312" data-mp="b3qxh6nfsgc5">
                <ChevronRightIcon className="w-6 h-6" data-id="element-2313" data-mp="2hpwyq82pql3i" />
              </button>

              <div id="pharmacy-services-slider" ref={sliderRef} onMouseDown={handleMouseDown} onMouseLeave={handleMouseLeave} onMouseUp={handleMouseUp} onMouseMove={handleMouseMove} className={`flex flex-nowrap overflow-x-auto ${isDragging ? 'cursor-grabbing select-none snap-none' : 'cursor-grab snap-x snap-mandatory'} scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4 pb-4 md:pb-0`} data-id="element-2314" data-mp="2mq1ge1fcxlvb">
                {smallCards.map((card, idx) => <button key={idx} onClick={() => handleCardClick(card.link)} className={`flex-shrink-0 w-[130px] sm:w-[140px] md:w-auto snap-start bg-white rounded-xl md:rounded-2xl p-3 md:p-4 flex flex-col items-center text-center hover:shadow-md transition-all hover:-translate-y-1 relative pt-7 md:pt-8 border border-transparent hover:border-green-100 ${isDragging ? 'pointer-events-none' : 'pointer-events-auto'}`} data-id="element-2315" data-mp="1b9kvvtoy4f1l" data-mpi={idx}>
                    {card.badge && <motion.span animate={{
                  scale: [1, 1.05, 1],
                  boxShadow: ['0 0 0 0px rgba(0, 0, 0, 0)', '0 0 0 4px rgba(0, 0, 0, 0.05)', '0 0 0 0px rgba(0, 0, 0, 0)']
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className={`absolute top-2 right-2 ${card.badge.color} text-white text-[10px] md:text-xs font-black px-2.5 py-0.5 rounded-full shadow-sm uppercase tracking-wider`} data-id="element-2316" data-mp="iomgjzpcboba">
                        {card.badge.text}
                      </motion.span>}
                    <motion.div whileHover={{
                  scale: 1.1,
                  rotate: 5
                }} className={`w-12 h-12 md:w-16 md:h-16 ${card.iconBg} rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-inner relative group-hover:shadow-md transition-all select-none pointer-events-none`} data-id="element-2317" data-mp="129d0apsr86uq">
                      <div className="absolute inset-0 bg-white/40 rounded-full scale-0 group-hover:scale-100 transition-transform duration-300" data-id="element-2318" data-mp="jasbx5i8zfks"></div>
                      {card.icon}
                    </motion.div>
                    <h3 className="text-xs md:text-sm font-bold text-gray-900 leading-tight whitespace-pre-line" data-id="element-2319" data-mp="25qq9j6tg87sk">
                      {card.title}
                    </h3>
                  </button>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}